import Image from 'next/image';
import { useEffect, useRef } from 'react';

// New component that implements the same approach as GiftCardVideo
interface WishlistsVideoProps {
  videoSrc: string;
  imageFallbackSrc?: string;
  isMobile: boolean;
}

const WishlistsVideo: React.FC<WishlistsVideoProps> = ({
  videoSrc,
  imageFallbackSrc,
  isMobile,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (!imageFallbackSrc) {
      const handleCanPlay = () => {
        videoElement.pause();
        videoElement.currentTime = 0;

        setTimeout(() => {
          if (document.visibilityState === 'visible') {
            videoElement.play().catch(() => {});
          }
        }, 100);
      };

      videoElement.addEventListener('canplay', handleCanPlay, { once: true });

      return () => {
        videoElement.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, [imageFallbackSrc]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          videoElement.play().catch(() => {
            // Silently fail if autoplay is blocked
          });
        } else {
          videoElement.pause();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(videoElement.parentElement as Element);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      className={`w-full mx-auto my-4 ${
        isMobile ? 'max-w-[300px] max-h-[300px]' : 'max-w-[480px] max-h-[480px]'
      }`}>
      {/* Hidden canvas for thumbnail generation */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        className={!isMobile ? 'w-full h-auto mx-auto' : undefined}
        poster={imageFallbackSrc || undefined}>
        <source src={videoSrc} type="video/mp4" />
        {imageFallbackSrc && (
          <Image
            src={imageFallbackSrc}
            alt="Wishlist video fallback"
            layout="responsive"
            width={480}
            height={480}
          />
        )}
      </video>
    </div>
  );
};

export default WishlistsVideo;
